import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import * as XLSX from 'xlsx';
import i18next from 'i18next';
import clsx from 'clsx';

import { Sidebar } from 'primereact/sidebar';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Paginator } from 'primereact/paginator';
import { Skeleton } from 'primereact/skeleton';
import { ToggleButton } from 'primereact/togglebutton';

import axiosInstance from '../config/axiosConfig';
import useSelectedCompanyStore from '../store/useSelectedCompanyStore';

const t = (i18nKey, params) => i18next.t(`userDashboard.candidate.invitation_sidebar.${i18nKey}`, params);

const CandidatesInvitationSidebar = ({ sidebarVisible, closeSidebar, toast }) => {
    const [newInvitation, setNewInvitation] = useState({ max_uses: 999, predefined_name: '', email: '', send_email: true });
    const [uploads, setUploads] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [invitationLoading, setInvitationLoading] = useState(true);
    const [limitFilter, setLimitFilter] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [isEmailValid, setIsEmailValid] = useState(true);

    const { campaign_id } = useParams();

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);

    const fetchInvitations = useCallback(async () => {
        if (!selectedCompany) return;

        try {
            const response = await axiosInstance.get(`/dashboard/invitation/${selectedCompany?.company_id}/${campaign_id}/list`, {
                params: { limit: limitFilter, page: currentPage },
            });
            setInvitations(response.data.items);
            setTotalItems(response.data.count || 0);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la récupération des invitations.', life: 3000 });
        } finally {
            setInvitationLoading(false);
        }
    }, [selectedCompany, campaign_id, limitFilter, currentPage, toast]);

    useEffect(() => {
        fetchInvitations();
    }, [fetchInvitations]);

    const saveNewInvitation = async (invitationData) => {
        try {
            await axiosInstance.post(`/dashboard/campaigns/${selectedCompany?.company_id}/${campaign_id}/invitations`, invitationData);
            fetchInvitations();
            toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Invitation créée avec succès', life: 3000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: "Une erreur est survenue pendant la création de l'invitation", life: 3000 });
        }
    };

    const sendEmailInvitation = async (invitation_id) => {
        try {
            await axiosInstance.post(`/dashboard/invitation/${selectedCompany?.company_id}/${campaign_id}/send-email-invitation/${invitation_id}`);
            toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Invitation envoyée par email avec succès', life: 3000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: "Une erreur est survenue lors de l'envoi de l'email", life: 3000 });
        }
    };

    const handleSaveUploadedInvitations = async (uploadId) => {
        const upload = uploads.find((u) => u.id === uploadId);
        if (!upload) return;

        const bulkInvitationData = upload.data.map(candidate => ({
            max_uses: 1,
            predefined_name: candidate.reference,
            email: candidate.email,
            send_email: false
        }));

        try {
            await axiosInstance.post(`/dashboard/campaigns/${selectedCompany?.company_id}/${campaign_id}/bulk-invitations`, { invitations: bulkInvitationData });
            fetchInvitations();
            toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Invitations créées avec succès', life: 3000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: "Une erreur est survenue pendant la création des invitations", life: 3000 });
        }

        setUploads((prevUploads) => prevUploads.filter((u) => u.id !== uploadId));
    };

    const deleteInvitation = async (invitation_id) => {
        try {
            await axiosInstance.delete(`/dashboard/invitation/${selectedCompany?.company_id}/${invitation_id}/delete`);
            fetchInvitations();
            toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Invitation supprimée avec succès', life: 3000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Une erreur est survenue pendant la suppression de cette invitation', life: 3000 });
        }
    };

    const copyLink = async (invitation_code) => {
        const link = `${window.location.origin}/tests/campaign/${invitation_code}/start`;
        if (!navigator.clipboard) {
            const textarea = document.createElement('textarea');
            textarea.value = link;
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
                toast.current.show({ severity: 'info', summary: 'Lien copié', detail: 'Le lien de l\'invitation a été copié dans le presse-papiers.', life: 3000 });
            } catch (error) {
                console.error(error);
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de la copie du lien.', life: 3000 });
            }
            document.body.removeChild(textarea);
        } else {
            try {
                await navigator.clipboard.writeText(link);
                toast.current.show({ severity: 'info', summary: 'Lien copié', detail: 'Le lien de l\'invitation a été copié dans le presse-papiers.', life: 3000 });
            } catch (error) {
                console.error(error);
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de la copie du lien.', life: 3000 });
            }
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const validExtensions = ['csv', 'xlsx', 'xls'];
        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Le fichier doit être au format csv, xls ou xlsx.', life: 3000 });
            return;
        }

        const uploadId = new Date().getTime();
        const newUpload = {
            id: uploadId,
            file: file,
            data: [],
        };

        setUploads(prevUploads => [...prevUploads, newUpload]);

        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            const headers = jsonData[0];
            if (headers[0] !== 'email' || headers[1] !== 'reference') {
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Le fichier doit contenir les colonnes "email" et "reference".', life: 3000 });
                return;
            }

            const validData = jsonData.slice(1).map(row => ({
                email: row[0],
                reference: row[1],
            }));

            setUploads(prevUploads =>
                prevUploads.map(u =>
                    u.id === uploadId ? { ...u, data: validData } : u
                )
            );

            toast.current.show({ severity: 'info', summary: 'Importation réussie', detail: 'Fichier prêt pour l\'importation.', life: 3000 });
        };

        reader.readAsArrayBuffer(file);
    };

    const handleRemoveFile = (uploadId) => {
        setUploads(prevUploads => prevUploads.filter(upload => upload.id !== uploadId));
    };

    const handleEmailChange = (e) => {
        const email = e.target.value;
        setNewInvitation((prev) => ({ ...prev, email }));

        // Email validation regex pattern
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        setIsEmailValid(email === '' || emailPattern.test(email));
    };

    return (
        <Sidebar className='is-width-8' header={(<h1 className="title is-4">{t('title')}</h1>)} visible={sidebarVisible} position="right" onHide={closeSidebar}>

            <Card title={t('card_title')}>
                <div className="field">
                    <label htmlFor="max_uses" className="label">
                        {t('max_usage')} (1 - 999)
                        <Tooltip target=".max-uses-info" content="Nombre de participants pouvant être invités avec un lien unique" position="top" mouseTrack
                            pt={{
                                arrow: {
                                    style: { display: 'none' }
                                }
                            }}
                        />
                        <i className="pi pi-info-circle max-uses-info is-clickable" style={{ fontSize: '1em', marginLeft: '0.5em' }} />
                    </label>
                    <div className="control">
                        <div className="flex-auto">
                            <InputNumber
                                inputId="max_uses"
                                value={newInvitation.max_uses}
                                onValueChange={(e) => setNewInvitation({ ...newInvitation, max_uses: e.value })}
                                mode="decimal"
                                min={1} max={999}
                                showButtons
                                disabled={newInvitation.email !== ''}
                            />
                        </div>
                    </div>
                </div>

                <hr className="is-invisible" />

                <div className="field">
                    <label htmlFor="email" className="label">
                        {t('invitation_email')}
                        <Tooltip target=".invitation_email" content={t('invitation_email_tooltip')} position="top" mouseTrack
                            pt={{
                                arrow: {
                                    style: { display: 'none' }
                                }
                            }}
                        />
                        <i className="pi pi-info-circle invitation_email is-clickable" style={{ fontSize: '1em', marginLeft: '0.5em' }} />
                    </label>

                    <div className="control">
                        <InputText id="email" value={newInvitation.email} onChange={handleEmailChange} className={`input ${!isEmailValid ? 'is-danger' : ''}`} />
                        {!isEmailValid && <p className="help is-danger">{t('invalid_email')}</p>}
                    </div>
                </div>

                <div className="field">
                    <ToggleButton
                        className='invitation-toggle-button'
                        checked={newInvitation.send_email}
                        onChange={(e) => setNewInvitation({ ...newInvitation, send_email: e.value })}
                        onLabel={t('email_toggle_button')}
                        offLabel={t('email_toggle_button')}
                        onIcon="pi pi-check" offIcon="pi pi-times"
                    />
                </div>

                <div className="field">
                    <label htmlFor="predefined_name" className="label">
                        {t('invitation_reference')}
                        <Tooltip target=".predefined-name-info" content={t('invitation_reference_tooltip')} position="top" mouseTrack
                            pt={{
                                arrow: {
                                    style: { display: 'none' }
                                }
                            }}
                        />
                        <i className="pi pi-info-circle predefined-name-info is-clickable" style={{ fontSize: '1em', marginLeft: '0.5em' }} />
                    </label>
                    <div className="control">
                        <InputText id="predefined_name" value={newInvitation.predefined_name} onChange={(e) => setNewInvitation({ ...newInvitation, predefined_name: e.target.value })} className="input" />
                    </div>
                </div>

                <div className="field mt-2">
                    <a className="button is-info is-light is-outlined" onClick={() => saveNewInvitation(newInvitation)} disabled={!isEmailValid}>
                        <span className="icon">
                            <i className="fa-solid fa-check"></i>
                        </span>
                        <span>{t('register')}</span>
                    </a>
                </div>
            </Card>

            <hr className="is-invisible" />

            <Card title={t('import_participants_label')}>
                <div className="field">
                    <label className="label" dangerouslySetInnerHTML={{ __html: t('invitation_import_label') }}></label>

                    <div className="file is-info">
                        <label className="file-label">
                            <input className="file-input" type="file" name="file" accept=".csv, .xlsx, .xls" onChange={handleFileSelect} />

                            <span className="file-cta">
                                <span className="file-icon">
                                    <i className="fas fa-upload"></i>
                                </span>
                                <span className="file-label"> {t('choose_file')} </span>
                            </span>
                        </label>
                    </div>

                    {uploads.map((upload) => (
                        <div key={upload.id} className="box dark-shadow table-container mt-4">
                            <h1 className='title is-5'><strong>{upload.file.name}</strong></h1>
                            <table className="table is-striped is-fullwidth">
                                <thead>
                                    <tr>
                                        <th>{t('email')}</th>
                                        <th>{t('reference')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {upload.data.slice(0, 3).map((data, index) => (
                                        <tr key={index}>
                                            <td>{data.email}</td>
                                            <td>{data.reference}</td>
                                        </tr>
                                    ))}
                                    {upload.data.length > 3 && (
                                        <tr>
                                            <td colSpan="2" className="has-text-centered">
                                                + {upload.data.length - 3} {t('other_participants')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            <div className="buttons">
                                <button className="button is-info mt-2" onClick={() => handleSaveUploadedInvitations(upload.id)}>{t('register')}</button>
                                <button className="button is-danger mt-2" onClick={() => handleRemoveFile(upload.id)}>{t('remove_list')}</button>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            <hr className="is-invisible" />

            <Card title={t('invitation_card.title')}>
                {invitationLoading ? (
                    <>
                        <Skeleton width="100%" height="2rem" className="mb-2" />
                        <Skeleton width="100%" height="2rem" className="mb-2" />
                        <Skeleton width="100%" height="2rem" className="mb-2" />
                        <Skeleton width="100%" height="2rem" className="mb-2" />
                    </>
                ) : invitations?.length > 0 ? (
                    <>
                        {invitations.map((invitation) => {
                            const shadowClass = clsx({
                                'shadow-yellow': invitation.used_count > 0 && invitation.used_count < invitation.max_uses,
                                'shadow-green': invitation.used_count >= invitation.max_uses,
                            });

                            return (
                                <div key={invitation.invitation_id} className={`box is-flex is-align-items-center is-justify-content-space-between mb-2 ${shadowClass}`}>
                                    <div>
                                        <p>{t('invitation_card.reference')} - <strong>{invitation.predefined_name || t('invitation_card.unassigned')}</strong></p>
                                        <p>{t('invitation_card.email')} - <strong>{invitation.email || t('invitation_card.unprovided')}</strong></p>
                                        <p>{t('invitation_card.code')} - {invitation.invitation_code}</p>
                                        <p>{t('invitation_card.usage')} - {invitation.used_count} / {invitation.max_uses}</p>

                                        <Button
                                            icon="pi pi-copy"
                                            className="p-button-rounded p-button-info p-mr-2 mt-2"
                                            onClick={() => copyLink(invitation.invitation_code)}
                                            tooltipOptions={{ position: 'left', mouseTrack: true, mouseTrackLeft: 5 }}
                                            tooltip={`${t('invitation_card.copy_link')} : ${window.location.origin}/tests/campaign/${invitation.invitation_code}/start`}
                                        />
                                        <Button
                                            label={t('invitation_card.send_invitation_email')}
                                            icon="pi pi-send"
                                            className="p-button-rounded p-button-info send-email ml-2 mt-2"
                                            onClick={() => sendEmailInvitation(invitation.invitation_id)}
                                            disabled={!invitation.email}
                                            outlined={!invitation.email}
                                            tooltipOptions={{ position: 'left', mouseTrack: true, mouseTrackLeft: 5 }}
                                            tooltip={invitation.email ? t('invitation_card.send_email') : t('invitation_card.unprovided')}
                                        />
                                    </div>
                                    <Button
                                        label={i18next.t('commons.delete')}
                                        icon="pi pi-trash"
                                        className="button is-danger is-rounded"
                                        onClick={() => deleteInvitation(invitation.invitation_id)}
                                    />
                                </div>
                            );
                        })}
                        <Paginator first={(currentPage - 1) * limitFilter} rows={limitFilter} totalRecords={totalItems} onPageChange={(e) => setCurrentPage(e.page + 1)} />
                    </>
                ) : (
                    <p>{t('invitation_card.invitation_empty')}</p>
                )}
            </Card>
        </Sidebar>
    );
};

export default CandidatesInvitationSidebar;